import { CliveStoreProcessingStatus } from '~/components/Clive/StatusChip/CliveStoreStatusChip';
import { reportErrorInProductionOrThrow } from '~/Utils';

// Single source of truth for panel messages
const PANEL_MESSAGES = {
  UNSUPPORTED_FILE_TYPE: {
    title: 'Unsupported File',
    subtitle: "I can't process this file type.",
  },
  FILE_TOO_LARGE: {
    title: 'Unsupported File',
    subtitle: 'This file is too large for me to process.',
  },
  UPLOAD_FAILED: {
    title: 'Classification Failed',
    subtitle: "I couldn't classify this document.",
  },
  DEFAULTS_APPLIED: {
    title: 'Document Analysis Available - Partial Classification',
    // subtitle is dynamic based on defaulted fields
  },
  SUCCESS: {
    title: 'Document Analysis Available',
    subtitle: 'I analyzed this document and generated a summary.',
  },
  ERROR_FALLBACK: {
    title: 'Failed to Process',
    subtitle: "I wasn't able to process this document.",
  },
  INVOICE_READY: {
    title: "Clive's Analysis Available - New Invoice ready for review",
    subtitle: 'Analysis complete. You can now review, edit, or approve the extracted invoice data.',
  },
} as const;

// Canned copy for a created document that then failed, keyed by status. Membership is the branch
// test: a status that is not in here is not one of these failures.
const CREATED_DOCUMENT_ERROR_MESSAGES: Record<string, { readonly title: string; readonly subtitle: string }> = {
  [CliveStoreProcessingStatus.ERROR_UNSUPPORTED_FILE_TYPE]: PANEL_MESSAGES.UNSUPPORTED_FILE_TYPE,
  [CliveStoreProcessingStatus.ERROR_FILE_TOO_LARGE]: PANEL_MESSAGES.FILE_TOO_LARGE,
  [CliveStoreProcessingStatus.ERROR]: PANEL_MESSAGES.ERROR_FALLBACK,
};

interface CliveStoreInformation {
  processing_status?: string;
  short_description?: string;
  full_summary?: string;
  deep_analysis_status?: string;
}

interface DocumentExtra {
  clive_defaults_applied?: string[];
  created_by_ai?: boolean;

  [key: string]: unknown;
}

interface CliveActionInfo {
  id: number;
  task_type: string;
  status: string;
}

export interface DocumentWithCliveInfo {
  clive_store_information?: CliveStoreInformation;
  clive_actions?: CliveActionInfo[];
  is_uploading?: boolean;
  stored_file_id?: number;
  document_extra?: DocumentExtra | Record<string, unknown>;
  document_name?: string;
  // Clive's own description, rendered by the documents table's Summary column. On a failure it is
  // the one accurate account of what went wrong.
  summary?: string;
  id?: number;

  [key: string]: unknown; // Allow other properties from DocumentMetaData
}

export interface ClivePanelContent {
  panelTitle: string;
  panelSubtitle: string;
  avatarType: 'happy' | 'error';
  isUploadFailed: boolean;
  isUnrecognizedType: boolean;
  showClassifyButton: boolean;
  showReviewButton: boolean;
  pendingInvoiceAction: CliveActionInfo | null;
}

/**
 * Determines the Clive panel content (title, subtitle, avatar type) based on document processing status.
 * This helper is shared between DocumentSidePanel and notification displays.
 *
 * Priority order:
 * 1. Pending invoice action → shows "Invoice ready for review" with Review button
 * 2. Error states → shows error message with optional Classify button
 * 3. Success → shows "Document Analysis Available"
 *
 * @param document - The document with Clive information
 * @returns Panel content configuration (including status flags) or default values on error
 */
export function getClivePanelContent(document: DocumentWithCliveInfo | undefined | null): ClivePanelContent {
  try {
    if (!document) {
      return {
        panelTitle: PANEL_MESSAGES.ERROR_FALLBACK.title,
        panelSubtitle: PANEL_MESSAGES.ERROR_FALLBACK.subtitle,
        avatarType: 'error',
        isUploadFailed: false,
        isUnrecognizedType: false,
        showClassifyButton: false,
        showReviewButton: false,
        pendingInvoiceAction: null,
      };
    }

    // Check for pending invoice action first - highest priority
    const pendingInvoiceAction =
      document.clive_actions?.find(
        (a) => a.task_type === 'invoice_analysis_recommendation' && a.status === 'pending'
      ) || null;

    if (pendingInvoiceAction) {
      return {
        panelTitle: PANEL_MESSAGES.INVOICE_READY.title,
        panelSubtitle: PANEL_MESSAGES.INVOICE_READY.subtitle,
        avatarType: 'happy',
        isUploadFailed: false,
        isUnrecognizedType: false,
        showClassifyButton: false,
        showReviewButton: true,
        pendingInvoiceAction,
      };
    }

    const hasDocument = !document?.is_uploading;
    const processingStatus = document?.clive_store_information?.processing_status;
    const needsClassification = processingStatus === CliveStoreProcessingStatus.DEFAULTS_APPLIED;
    const isUnrecognizedType = Boolean(processingStatus === CliveStoreProcessingStatus.ERROR_CLASSIFYING_DOCUMENT_TYPE);
    const isUploadFailed = Boolean(
      !hasDocument &&
        (processingStatus === CliveStoreProcessingStatus.ERROR ||
          processingStatus === CliveStoreProcessingStatus.ERROR_UPLOADING ||
          processingStatus === CliveStoreProcessingStatus.ERROR_UNSUPPORTED_FILE_TYPE ||
          processingStatus === CliveStoreProcessingStatus.ERROR_FILE_TOO_LARGE)
    );

    // For uploading documents (not yet created): Always show generic "Failed to Classify" message
    if (!hasDocument && (isUploadFailed || isUnrecognizedType)) {
      let title = '';
      let subtitle = '';
      if (processingStatus === CliveStoreProcessingStatus.ERROR_UNSUPPORTED_FILE_TYPE) {
        title = PANEL_MESSAGES.UNSUPPORTED_FILE_TYPE.title;
        subtitle = PANEL_MESSAGES.UNSUPPORTED_FILE_TYPE.subtitle;
      } else if (processingStatus === CliveStoreProcessingStatus.ERROR_FILE_TOO_LARGE) {
        title = PANEL_MESSAGES.FILE_TOO_LARGE.title;
        subtitle = PANEL_MESSAGES.FILE_TOO_LARGE.subtitle;
      } else {
        title = PANEL_MESSAGES.UPLOAD_FAILED.title;
        subtitle = PANEL_MESSAGES.UPLOAD_FAILED.subtitle;
      }
      // Manual classification posts `stored_file_id` to retry_failed_upload and the server builds
      // the document from it, so a row that carries no stored file has nothing to offer: the
      // request would go out without the key and come back a 500. Failed email attachments are
      // such a row -- their file is reached from the communications tab, not from here -- so they
      // get the honest failure message without the affordance.
      const canClassifyManually = Boolean(document?.stored_file_id);
      if (canClassifyManually) {
        subtitle += ' Please complete the classification manually to finish the upload.';
      }
      return {
        panelTitle: title,
        panelSubtitle: subtitle,
        avatarType: 'error',
        isUploadFailed,
        isUnrecognizedType,
        showClassifyButton: canClassifyManually,
        showReviewButton: false,
        pendingInvoiceAction: null,
      };
    }

    // For an already created document, prefer its own description over the canned copy. The canned
    // copy is re-derived from an error code that cannot carry the distinction it claims -- a corrupt
    // PDF and a file type we have no processor for both arrive as `error_unsupported_file_type` --
    // so it contradicts the sentence Clive wrote onto the document and the table already shows.
    // The title drops with it, since "Unsupported File" is the claim the code cannot establish.
    // hasOwn, not a bare lookup: `processing_status` is an arbitrary string off the wire, and a
    // plain object answers to `constructor` and `toString` with something truthy.
    const cannedErrorMessage =
      processingStatus && Object.hasOwn(CREATED_DOCUMENT_ERROR_MESSAGES, processingStatus)
        ? CREATED_DOCUMENT_ERROR_MESSAGES[processingStatus]
        : undefined;
    if (hasDocument && cannedErrorMessage) {
      const documentDescription = document.summary?.trim() || '';

      return {
        panelTitle: documentDescription ? PANEL_MESSAGES.ERROR_FALLBACK.title : cannedErrorMessage.title,
        panelSubtitle: documentDescription || cannedErrorMessage.subtitle,
        avatarType: 'error',
        isUploadFailed: false,
        isUnrecognizedType: false,
        showClassifyButton: false,
        showReviewButton: false,
        pendingInvoiceAction: null,
      };
    }

    if (needsClassification) {
      const defaultedFields = document?.document_extra?.clive_defaults_applied;
      const defaultedFieldsArray = Array.isArray(defaultedFields) ? defaultedFields : [];
      const defaultedFieldsText = defaultedFieldsArray.length > 0 ? defaultedFieldsArray.join(', ') : '';

      return {
        panelTitle: PANEL_MESSAGES.DEFAULTS_APPLIED.title,
        panelSubtitle: `I analyzed this document and generated a summary. Unfortunately, I couldn't determine values for the following fields: ${defaultedFieldsText}. Default values were added — you can edit them anytime from the three-dot menu.`,
        avatarType: 'happy',
        isUploadFailed,
        isUnrecognizedType,
        showClassifyButton: false,
        showReviewButton: false,
        pendingInvoiceAction: null,
      };
    }

    return {
      panelTitle: PANEL_MESSAGES.SUCCESS.title,
      panelSubtitle: PANEL_MESSAGES.SUCCESS.subtitle,
      avatarType: 'happy',
      isUploadFailed,
      isUnrecognizedType,
      showClassifyButton: false,
      showReviewButton: false,
      pendingInvoiceAction: null,
    };
  } catch (error) {
    reportErrorInProductionOrThrow(error);
    // Return default values on error to prevent crash
    return {
      panelTitle: PANEL_MESSAGES.ERROR_FALLBACK.title,
      panelSubtitle: PANEL_MESSAGES.ERROR_FALLBACK.subtitle,
      avatarType: 'happy',
      isUploadFailed: false,
      isUnrecognizedType: false,
      showClassifyButton: false,
      showReviewButton: false,
      pendingInvoiceAction: null,
    };
  }
}
